import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SecretInput } from '@/components/form/secret-input'
import { useToast } from '@/components/ui/use-toast'

const PaystackEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const processorExit = useProcessorEditExit()
    const { t } = useTranslation('processors')
    const { toast } = useToast()

    const { data: processor, isLoading } = useProcessorById(id!)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        testMode: false,
        dryMode: false,
        secretKey: '',
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                testMode: options?.testMode ?? false,
                dryMode: options?.dryMode === true,
                secretKey: options?.secretKey || '',
            })
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) { setValidationError(t('nameRequired')); return }
        if (!formData.secretKey.trim()) { setValidationError(t('paystack.credentials.secretKeyRequired')); return }

        setValidationError(null)
        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    name: formData.name,
                    type: 'paystack',
                    enabled: formData.enabled,
                    options: {
                        testMode: formData.testMode,
                        dryMode: formData.dryMode,
                        secretKey: formData.secretKey,
                    },
                } as any,
            })
            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch {
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    if (isLoading) return null

    if (!processor || processor.type !== 'paystack') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('paystack.editTitle')}
                onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <Card>
                    <CardHeader><CardTitle>{t('generalSettings')}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input id="name" value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="enabled" checked={formData.enabled}
                                onCheckedChange={(c) => setFormData({ ...formData, enabled: c === true })} />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader><CardTitle>{t('apiCredentials')}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('paystack.credentials.howToGetTitle')}</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('paystack.credentials.step1')}</li>
                                <li>{t('paystack.credentials.step2')}</li>
                                <li>{t('paystack.credentials.step3')}</li>
                                <li>{t('paystack.credentials.step4')}</li>
                            </ol>
                        </div>
                        <SecretInput label={t('paystack.credentials.secretKeyLabel') + ' *'} value={formData.secretKey}
                            onChange={(v) => setFormData({ ...formData, secretKey: v })}
                            placeholder={t('paystack.credentials.secretKeyPlaceholder')} />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader><CardTitle>{t('testModeSettings')}</CardTitle></CardHeader>
                    <CardContent>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="testMode" checked={formData.testMode}
                                onCheckedChange={(c) => setFormData({ ...formData, testMode: c === true })} />
                            <Label htmlFor="testMode">{t('enableTestMode')}</Label>
                        </div>
                        {formData.testMode && (
                            <div className="mt-3 rounded-lg bg-yellow-50 p-4 border border-yellow-200">
                                <p className="text-sm text-yellow-700">
                                    {t('paystack.testMode.description')}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(c) => setFormData({ ...formData, dryMode: c })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
        </div>
    )
}

export const PaystackEditPage = withRequiredPermission(PaystackEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
